import { currentLook, spriteDirectory } from './look';

export interface SpriteDefinition {
  readonly id: string;
  readonly file: string;
  readonly group: string;
  readonly width: number;
  readonly height: number;
}

export interface SpriteGroupWeight {
  readonly group: string;
  readonly weight: number;
}

export interface SpriteCatalog {
  readonly sprites: readonly SpriteDefinition[];
  readonly groups: readonly SpriteGroupWeight[];
  readonly byGroup: ReadonlyMap<string, readonly SpriteDefinition[]>;
}

export const EMPTY_SPRITE_CATALOG: SpriteCatalog = {
  sprites: [],
  groups: [],
  byGroup: new Map(),
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function positiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function spriteFromJson(raw: unknown): SpriteDefinition | null {
  if (!isRecord(raw)) return null;
  const { id, file, group } = raw;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof file !== 'string' || file.length === 0) return null;
  if (typeof group !== 'string' || group.length === 0) return null;

  const width = positiveNumber(raw.width);
  const height = positiveNumber(raw.height);
  if (width === null || height === null) return null;

  return { id, file, group, width, height };
}

function weightsFor(
  groupsInUse: readonly string[],
  declared: Record<string, unknown> | null,
): SpriteGroupWeight[] {
  const raw = groupsInUse.map((group) => ({
    group,
    weight: (declared && positiveNumber(declared[group])) ?? 1,
  }));
  const total = raw.reduce((sum, entry) => sum + entry.weight, 0);
  if (total <= 0) return groupsInUse.map((group) => ({ group, weight: 1 / groupsInUse.length }));
  return raw.map((entry) => ({ group: entry.group, weight: entry.weight / total }));
}

export function parseManifest(raw: unknown): SpriteCatalog {
  if (!isRecord(raw) || !Array.isArray(raw.sprites)) return EMPTY_SPRITE_CATALOG;

  const sprites = raw.sprites
    .map(spriteFromJson)
    .filter((sprite): sprite is SpriteDefinition => sprite !== null);
  if (sprites.length === 0) return EMPTY_SPRITE_CATALOG;

  const byGroup = new Map<string, SpriteDefinition[]>();
  for (const sprite of sprites) {
    const bucket = byGroup.get(sprite.group);
    if (bucket) bucket.push(sprite);
    else byGroup.set(sprite.group, [sprite]);
  }

  const groupsInUse = [...byGroup.keys()].sort();
  const declared = isRecord(raw.groups) ? raw.groups : null;

  return { sprites, groups: weightsFor(groupsInUse, declared), byGroup };
}

function clampRoll(roll: number): number {
  if (!Number.isFinite(roll)) return 0;
  return Math.min(0.9999999, Math.max(0, roll));
}

export function pickSprite(
  catalog: SpriteCatalog,
  groupRoll: number,
  spriteRoll: number,
): SpriteDefinition | null {
  if (catalog.groups.length === 0) return null;

  let remaining = clampRoll(groupRoll);
  let chosen = catalog.groups[catalog.groups.length - 1]?.group ?? null;
  for (const entry of catalog.groups) {
    if (remaining < entry.weight) {
      chosen = entry.group;
      break;
    }
    remaining -= entry.weight;
  }
  if (chosen === null) return null;

  const bucket = catalog.byGroup.get(chosen);
  if (!bucket || bucket.length === 0) return null;

  const index = Math.min(bucket.length - 1, Math.floor(clampRoll(spriteRoll) * bucket.length));
  return bucket[index] ?? null;
}

let catalog: SpriteCatalog = EMPTY_SPRITE_CATALOG;
let revision = 0;
let pending: Promise<SpriteCatalog> | null = null;
const images = new Map<string, CanvasImageSource>();
const listeners = new Set<() => void>();

export function spriteCatalog(): SpriteCatalog {
  return catalog;
}

export function spriteRevision(): number {
  return revision;
}

export function spriteImage(id: string): CanvasImageSource | null {
  return images.get(id) ?? null;
}

export function onSpritesLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function spriteBaseUrl(): string {
  const base = import.meta.env?.BASE_URL;
  return typeof base === 'string' && base.length > 0 ? base : '/';
}

function loadImage(url: string, id: string): Promise<void> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      images.set(id, image);
      resolve();
    };
    image.onerror = () => resolve();
    image.src = url;
  });
}

async function fetchCatalog(): Promise<SpriteCatalog> {
  if (typeof document === 'undefined' || typeof fetch !== 'function') return EMPTY_SPRITE_CATALOG;

  const directory = `${spriteBaseUrl()}${spriteDirectory(currentLook())}`;
  const response = await fetch(`${directory}/manifest.json`);
  if (!response.ok) return EMPTY_SPRITE_CATALOG;

  const parsed = parseManifest(await response.json());
  if (parsed.sprites.length === 0) return EMPTY_SPRITE_CATALOG;

  if (typeof Image !== 'undefined') {
    await Promise.all(parsed.sprites.map((s) => loadImage(`${directory}/${s.file}`, s.id)));
  }
  return parsed;
}

export function ensureSpritesLoaded(): Promise<SpriteCatalog> {
  if (pending) return pending;

  pending = fetchCatalog()
    .catch(() => EMPTY_SPRITE_CATALOG)
    .then((loaded) => {
      if (loaded.sprites.length === 0) return EMPTY_SPRITE_CATALOG;
      catalog = loaded;
      revision++;
      for (const listener of listeners) listener();
      return loaded;
    });

  return pending;
}

export function resetSprites(): void {
  catalog = EMPTY_SPRITE_CATALOG;
  revision = 0;
  pending = null;
  images.clear();
  listeners.clear();
}
